import { t } from '@/features/i18n/utils';
import { createFileRoute, Link, notFound, redirect } from '@tanstack/react-router';
import { resolveGoCitation, resolveGoRedirect } from '@/features/teams/utils';
import z from 'zod';
import { useDocument } from '@/features/documents/queries';
import { DocumentPreviewToolbar } from '@/features/documents/preview/components/document-preview-toolbar';
import { DocumentViewer } from '@/features/documents/preview/components/document-viewer';
import { createPreviewState } from '@/features/documents/preview/store';
import { Spinner } from '@/ui/components/spinner';

export const Route = createFileRoute('/_authed/go/$entity/$entityId')({
  validateSearch: z.object({
    evalId: z.string().optional(),
    projectId: z.uuid().optional(),
    view: z.enum(['file']).optional(),
    page: z.coerce.number().optional(),
    threadId: z.uuid().optional(),
    messageId: z.uuid().optional(),
    citation: z.coerce.number().int().positive().optional(),
    channelSource: z.uuid().optional(),
  }),
  beforeLoad: async ({ context, params, search }) => {
    if (params.entity === 'document' && search.citation !== undefined) {
      const citation = await resolveGoCitation(context.queryClient, params.entityId, search);
      if (!citation) throw notFound();
      return { citation };
    }
    const target = await resolveGoRedirect(
      context.queryClient,
      params.entity,
      params.entityId,
      search,
    );
    if (!target) throw notFound();
    throw redirect({ ...target, replace: true });
  },
  notFoundComponent: GoNotFound,
  component: GoCitation,
});

function GoNotFound() {
  return (
    <div className="px-5 -translate-y-5 w-full">
      <div className="text-center">
        <p className="pb-3">
          {t({
            en: "This link is invalid or you don't have access.",
            nb: 'Denne lenken er ugyldig, eller du har ikke tilgang.',
          })}
        </p>
        <Link to="/" className="underline hover:text-foreground text-muted">
          {t({ en: 'Back to homepage', nb: 'Tilbake til hjemmesiden' })}
        </Link>
      </div>
    </div>
  );
}

function GoCitation() {
  const { citation } = Route.useRouteContext();
  const { view } = Route.useSearch();
  const { entityId } = Route.useParams();
  const { data: document, isPending, isError } = useDocument(entityId, citation?.projectId);
  if (isPending) return <Spinner kind="inset" />;
  if (isError || !document || !citation) return <GoNotFound />;
  const preview = createPreviewState({
    document,
    content: view === 'file' ? undefined : citation.content,
    source: view === 'file' ? undefined : citation.content,
    pageStart: view === 'file' ? undefined : citation.pageStart,
  });
  if (preview?.status !== 'loaded') return <GoNotFound />;
  return (
    <div className="flex h-full w-full min-h-0 flex-col">
      <div className="px-5 py-3 font-medium">{document.name}</div>
      {preview.url && <DocumentPreviewToolbar url={preview.url} filename={document.name} />}
      <div className="relative min-h-0 flex-1">
        <DocumentViewer
          url={preview.url}
          extension={preview.extension}
          query={preview.query}
          pageStart={preview.pageStart}
          content={preview.content}
        />
      </div>
    </div>
  );
}
